import type { ReactNode } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { MeProvider, useMe } from "./auth/MeProvider";
import { RequireAuth } from "./auth/RequireAuth";
import { AppShell } from "./components/AppShell";
import { Help, TicketThread } from "./routes/Help";
import { Home } from "./routes/Home";
import { Login } from "./routes/Login";
import { NotFound } from "./routes/NotFound";
import { Ops } from "./routes/Ops";
import { ResetPassword } from "./routes/ResetPassword";
import { Signup } from "./routes/Signup";
import { SupportAccess } from "./routes/SupportAccess";
import { TimeClock } from "./routes/TimeClock";
import { TimeReview } from "./routes/TimeReview";

/** Hides a page from roles that cannot use it. Cosmetic: the API checks again. */
function Gate({ allow, children }: { allow: (me: ReturnType<typeof useMe>) => boolean; children: ReactNode }) {
  const value = useMe();
  return allow(value) ? <>{children}</> : <Navigate to="/" replace />;
}

function SignedIn() {
  return (
    <MeProvider>
      <Outlet />
    </MeProvider>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<RequireAuth />}>
        <Route element={<SignedIn />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/time" element={<Gate allow={({ me }) => Boolean(me.role?.isStaff)}><TimeClock /></Gate>} />
            <Route path="/time/review" element={<Gate allow={({ isDirector }) => isDirector}><TimeReview /></Gate>} />
            <Route path="/help" element={<Gate allow={({ me }) => me.org !== null}><Help /></Gate>} />
            <Route path="/help/tickets/:id" element={<Gate allow={({ me }) => me.org !== null}><TicketThread /></Gate>} />
            <Route path="/settings/support-access" element={<Gate allow={({ isDirector }) => isDirector}><SupportAccess /></Gate>} />
            <Route path="/ops" element={<Gate allow={({ me }) => me.platformRole !== null}><Ops /></Gate>} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
